/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// seedForecast.js — backlog 141.
//
// A new tenant's forecast is a blank grid. A portfolio of 20 SKUs across 15
// states is 3,600 cells for ONE year, all typed by hand, inside a 14-day trial —
// so the module's own onboarding was the thing most likely to lose a prospect.
//
// The data to propose a starting point is already on the screen: the dashboard
// loads `rawActuals` (depletions) for the chart's Plan-vs-Actual mode and
// `rawForecast` for the grid. This planner turns either of those into a list of
// cell operations for a target year.
//
// PURE ON PURPOSE. It does no I/O and touches no React state, so the rules that
// are easy to get wrong — which cells count as empty, what a growth % does to a
// zero, which existing value an undo has to restore — are unit-testable without
// a database. Dashboard.handleSeedYear executes the plan; this decides it.

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Decide which cells a seed would write.
 *
 * @param {object}   a
 * @param {"actuals"|"forecast"} a.source     which body of numbers to read
 * @param {Array}    a.rawForecast            [{ id, fields }] — the grid's rows
 * @param {Array}    a.rawActuals             [{ fields }] — depletions, no ids
 * @param {string|number} a.sourceYear        year to read from
 * @param {string|number} a.targetYear        year to write into
 * @param {string}   a.state                  selectedState — required
 * @param {string}   [a.distributor]          selectedDistributor, "" = any
 * @param {number}   [a.distributorCount=0]   how many distributors serve `state`
 * @param {string[]} a.skus                   visible SKU names (already filtered)
 * @param {number}   [a.growthPct=0]          e.g. 5 → ×1.05, -10 → ×0.9
 * @param {boolean}  [a.overwrite=false]      false = only fill empty cells
 *
 * @returns {{ ops: Array, skipped: object, sourceCells: number }}
 *   ops: [{ sku, month, value, existingId|null, oldValue|null }]
 *   skipped: { occupied, zero, noSource }
 */
export function planSeed({
  source,
  rawForecast = [],
  rawActuals = [],
  sourceYear,
  targetYear,
  state,
  distributor = "",
  distributorCount = 0,
  skus = [],
  growthPct = 0,
  overwrite = false,
}) {
  const srcYear = String(sourceYear);
  const tgtYear = String(targetYear);
  const skipped = { occupied: 0, zero: 0, noSource: 0 };

  if (!state || !srcYear || !tgtYear || skus.length === 0) {
    return { ops: [], skipped, sourceCells: 0 };
  }

  // AGGREGATE VIEW — refuse. This is the grid's own rule, not a new one:
  // `ForecastGrid.jsx` computes
  //   isAggregate = !selectedState || (!selectedDistributor && distributorOptions.length > 1)
  // and makes every cell read-only when it holds, because a (sku, month) in a
  // two-distributor state maps to TWO forecast rows and nothing says which one a
  // number belongs to — `forecastAggregate` even sets `cell.id = null` there,
  // marking it "multiple records → not directly editable".
  //
  // The seed used to write anyway, and got both halves wrong: the target-side
  // map keyed on `sku|month`, so the second distributor's row silently replaced
  // the first, and `Dashboard.handleSeedYear` resolved its `stateRow` with a
  // bare `.find()` on state name, filing the summed total under whichever
  // distributor sorted first. Writing 240 cells unattended into a view the grid
  // will not let you type one character into is not a thing to make work — it
  // is a thing to refuse. Pick a distributor and the ambiguity is gone.
  if (!distributor && distributorCount > 1) {
    return { ops: [], skipped, sourceCells: 0, aggregate: true };
  }

  // Seeding a year from itself would read a cell and write it back over itself
  // — with a growth % applied, that is a silent compounding edit, not a seed.
  if (srcYear === tgtYear) {
    return { ops: [], skipped, sourceCells: 0, sameYear: true };
  }

  const inScope = (f) =>
    f.State === state &&
    (!distributor || f.Distributor === distributor);

  // ── Source values: (sku, month) → number ────────────────────────────────────
  // Depletions arrive per state+distributor row, so several rows can land on the
  // same (sku, month) once a state is served by more than one distributor and no
  // distributor filter is set. Sum them — that is the state's real volume, and
  // taking the last one silently under-forecasts.
  const src = new Map();
  const rows = source === "actuals" ? rawActuals : rawForecast;
  const valueKey = source === "actuals" ? "CaseActual" : "CaseForecast";

  for (const r of rows) {
    const f = r?.fields;
    if (!f || String(f.Year) !== srcYear || !inScope(f)) continue;
    if (!f.ProductSKU || !f.Month) continue;
    const key = `${f.ProductSKU}|${f.Month}`;
    src.set(key, (src.get(key) ?? 0) + (Number(f[valueKey]) || 0));
  }

  // ── Existing target cells: (sku, month) → { id, value } ─────────────────────
  // Unambiguous BECAUSE of the aggregate guard above: past it, either a
  // distributor is named or the state has at most one, and
  // `sales_states_account_state_dist_key unique (account_id, state_name,
  // distributor)` then makes (sku, month) map to exactly one forecast row.
  const existing = new Map();
  for (const r of rawForecast) {
    const f = r?.fields;
    if (!f || String(f.Year) !== tgtYear || !inScope(f)) continue;
    existing.set(`${f.ProductSKU}|${f.Month}`, {
      id: r.id,
      value: Number(f.CaseForecast) || 0,
    });
  }

  const factor = 1 + (Number(growthPct) || 0) / 100;
  const ops = [];
  const skuSet = new Set(skus);

  for (const sku of skuSet) {
    for (const month of MONTH_NAMES) {
      const key = `${sku}|${month}`;
      const base = src.get(key);
      if (base == null) { skipped.noSource++; continue; }

      // Whole cases. A forecast of 351.4 cases is false precision, and the grid
      // renders integers everywhere else.
      const value = Math.round(base * factor);

      // The grid itself refuses to create a cell at 0 (`parsed > 0` in
      // ForecastGrid.commitEdit) — a zero row is indistinguishable from no row
      // and just adds clutter. Match that rule rather than inventing a new one.
      if (!(value > 0)) { skipped.zero++; continue; }

      const hit = existing.get(key);
      if (hit && !overwrite) { skipped.occupied++; continue; }
      if (hit && hit.value === value) { skipped.occupied++; continue; }

      ops.push({
        sku,
        month,
        value,
        existingId: hit ? hit.id : null,
        oldValue: hit ? hit.value : null,
      });
    }
  }

  return { ops, skipped, sourceCells: src.size, overwrite: !!overwrite };
}

// ── Executing the plan (APP-522) ────────────────────────────────────────────
// The seed writes in ONE upsert instead of a round trip per cell. An upsert
// updates a conflicting row silently, where the old per-cell INSERT failed
// loudly on `forecasts_unique_cell` — so the two things that failure used to
// give us for free have to be computed here instead, against the DB's OWN
// picture of the cells (`forecastService.fetchForecastCells`) rather than the
// in-memory grid, which can be minutes stale:
//   1. "fill empty cells only" must hold against what exists NOW, or the fast
//      path overwrites a number the slow path refused to touch.
//   2. the undo record must know, per cell, whether the row is one we created
//      (undo deletes it) or one we changed (undo restores its old value). Get
//      this wrong and undo deletes a figure the user typed by hand.
// Pure, for the same reason `planSeed` is: this is the part that is easy to get
// wrong and expensive to get wrong.

/** Key a cell by the pair that is unique inside one (account, state, year). */
export const cellKey = (productId, month) => `${productId}|${month}`;

/**
 * Decide the exact cells to send, given what the DB currently holds.
 *
 * @param {object} a
 * @param {Array}  a.ops                 planSeed().ops
 * @param {boolean} [a.overwrite]        planSeed().overwrite
 * @param {Map<string,string>} a.productIdBySku
 * @param {Map<string,{id,caseForecast}>} a.priorByKey   cellKey → existing row
 * @returns {{ cells: Array, skippedOccupied: number, unknownSkus: string[] }}
 */
export function buildSeedWrite({ ops = [], overwrite = false, productIdBySku, priorByKey }) {
  const cells = [];
  const unknownSkus = [];
  let skippedOccupied = 0;

  for (const op of ops) {
    const productId = productIdBySku?.get(op.sku);
    if (!productId) { unknownSkus.push(op.sku); continue; }
    const key = cellKey(productId, op.month);
    const prior = priorByKey?.get(key);
    if (prior && !overwrite) { skippedOccupied++; continue; }
    // Nothing to write, and writing it anyway would put a no-op cell into the
    // undo record — an "undo" that restores the value it already had.
    if (prior && prior.caseForecast === op.value) { skippedOccupied++; continue; }
    cells.push({ productId, month: op.month, caseForecast: op.value, sku: op.sku });
  }

  return { cells, skippedOccupied, unknownSkus };
}

/**
 * Turn the rows the upsert returned into the undo record.
 *
 * `isNew` comes from the pre-read, never from the plan: a cell the plan
 * believed empty may have been filled since the grid loaded, and marking that
 * row "new" is what would make undo delete it.
 *
 * @param {object} a
 * @param {Array}  a.rows               bulkUpsertForecastCells().rows
 * @param {Map<string,{id,caseForecast}>} a.priorByKey
 * @param {Map<string,string>} a.skuByProductId
 * @returns {Array<{ sku, productId, month, value, id, isNew, oldValue, updatedAt }>}
 */
export function buildSeedOutcome({ rows = [], priorByKey, skuByProductId }) {
  return rows.map((r) => {
    const prior = priorByKey?.get(cellKey(r.productId, r.month));
    return {
      sku:       skuByProductId?.get(r.productId) ?? "",
      productId: r.productId,
      month:     r.month,
      value:     r.caseForecast,
      id:        r.id,
      isNew:     !prior,
      oldValue:  prior ? prior.caseForecast : null,
      updatedAt: r.updatedAt ?? null,
    };
  });
}

/** One-line human summary of what a plan will do. */
export function describePlan(plan) {
  if (!plan) return "";
  if (plan.sameYear) return "Pick a different source year — a year cannot seed itself.";
  if (plan.aggregate) {
    return "This state has more than one distributor — pick one. " +
      "A month would otherwise land on two forecast rows with nothing to say which.";
  }
  const { ops, skipped } = plan;
  if (ops.length === 0) {
    if (skipped.occupied > 0) return "Every cell in range already has a value.";
    // backlog APP-556 — "no source numbers" was a lie when there WERE numbers
    // and they all rounded away. Sending the user off to re-pick a source year
    // that was never the problem is worse than saying nothing.
    if (skipped.zero > 0) {
      return `Found ${skipped.zero.toLocaleString()} source value${skipped.zero === 1 ? "" : "s"}, ` +
        "but every one rounds to zero — raise the growth %, or pick a fuller source year.";
    }
    return "No source numbers found for that year and filter.";
  }
  const creates = ops.filter((o) => !o.existingId).length;
  const updates = ops.length - creates;
  const bits = [];
  if (creates) bits.push(`fill ${creates.toLocaleString()} empty cell${creates === 1 ? "" : "s"}`);
  if (updates) bits.push(`overwrite ${updates.toLocaleString()}`);
  if (skipped.occupied) bits.push(`leave ${skipped.occupied.toLocaleString()} already filled`);
  return `Will ${bits.join(", ")}.`;
}
